import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
} from '@mui/material';

const MIN_DROPDOWN_WIDTH = 120;

export enum SelectListType {
  Standard = 'standard',
  Filter = 'filter',
}

export type SelectOption = {
  value: string | number;
  name: string;
};

// Simplified theme data for custom overrides only
export class SelectListThemeData {
  width?: string;
  selectCssOverrides?: SxProps<Theme>;
  menuCssOverrides?: SxProps<Theme>;

  constructor({
    width = '100%',
    selectCssOverrides,
    menuCssOverrides,
  }: {
    width?: string;
    selectCssOverrides?: SxProps<Theme>;
    formControlCssOverrides?: SxProps<Theme>;
    menuCssOverrides?: SxProps<Theme>;
  } = {}) {
    this.width = width;
    this.selectCssOverrides = selectCssOverrides || {};
    this.menuCssOverrides = menuCssOverrides || {};
  }
}

export interface SelectListProps {
  name?: string;
  labelId?: string;
  label?: string;
  value?: string | number;
  options: SelectOption[];
  required?: boolean;
  disabled?: boolean;
  validationText?: string;
  warningText?: string;
  theme?: SelectListThemeData;
  type?: SelectListType;
  onChange?: (event: SelectChangeEvent<string>) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

export const SelectList: React.FC<SelectListProps> = React.memo(
  ({
    name,
    labelId,
    label,
    value,
    options,
    required = false,
    disabled = false,
    validationText,
    warningText,
    theme = new SelectListThemeData(),
    type = SelectListType.Standard,
    onChange,
    onBlur,
    fullWidth = true,
    ...props
  }) => {
    // Create a memoized styled component to prevent re-renders
    const SelectListWrapper = useMemo(
      () =>
        styled(Box)({
          width: theme.width,
          margin: 0,
          display: 'flex',
        }),
      [theme.width],
    );

    const error = validationText !== undefined && validationText !== '';
    const hasWarning = warningText !== undefined && warningText !== '';

    // Generate a unique ID if none provided
    const uniqueLabelId =
      labelId ||
      `select-label-${label?.replace(/\s+/g, '-').toLowerCase() || 'unknown'}`;

    // Memoize the options to prevent re-rendering when options haven't changed
    const memoizedOptions = useMemo(() => options, [options]);

    // ResizeObserver to track Select width for responsive dropdown
    const anchorRef = useRef<HTMLDivElement>(null);
    const [dropdownWidth, setDropdownWidth] = useState<number | undefined>();

    useEffect(() => {
      const anchorEl = anchorRef.current;
      if (!anchorEl) {
        return;
      }

      // Helper function to update width
      const updateWidth = () => {
        const currentEl = anchorRef.current;
        if (!currentEl) {
          return;
        }
        const rect = currentEl.getBoundingClientRect();
        if (rect.width) {
          setDropdownWidth(rect.width);
        }
      };

      // Check if ResizeObserver is available (not available in test environments)
      if (typeof ResizeObserver === 'undefined') {
        // Fallback: use window resize event for test environments
        updateWidth(); // Set initial width
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
      }

      const observer = new ResizeObserver(([entry]) => {
        if (entry.contentRect.width) {
          setDropdownWidth(entry.contentRect.width);
        }
      });

      observer.observe(anchorEl);

      return () => observer.disconnect();
    }, []); // Empty deps is fine - anchorRef is stable, and we check anchorRef.current inside updateWidth

    return (
      <SelectListWrapper>
        <FormControl
          required={required}
          disabled={disabled}
          error={error}
          variant='outlined'
          fullWidth={fullWidth}
          {...props}
        >
          <InputLabel
            id={uniqueLabelId}
            required={required}
            shrink={value !== undefined && value !== '' ? true : undefined}
          >
            {label}
          </InputLabel>
          <Box
            ref={anchorRef}
            sx={{
              width: '100%',
            }}
          >
            <Select
              name={name}
              labelId={uniqueLabelId}
              id={`${uniqueLabelId}-select`}
              value={value?.toString() ?? ''}
              label={label}
              IconComponent={ExpandMoreIcon}
              disabled={disabled}
              onChange={onChange}
              onBlur={onBlur}
              fullWidth
              // Note: autoWidth is required for ResizeObserver to work correctly.
              // While it may seem to conflict with fullWidth, fullWidth controls the
              // Select input width, while autoWidth enables the dropdown menu to
              // properly respond to the width set via MenuProps.slotProps.paper.sx.width
              autoWidth
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                slotProps: {
                  paper: {
                    sx: {
                      minWidth: MIN_DROPDOWN_WIDTH,
                      width: dropdownWidth ?? undefined,
                      // Only include custom overrides that would not be in the theme
                      ...theme.menuCssOverrides,
                    },
                  },
                },
              }}
              sx={{
                // Only include custom overrides that would not be in the theme
                ...theme.selectCssOverrides,
              }}
            >
              {memoizedOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {type === SelectListType.Standard ? (
                    option.name
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <Radio
                        checked={value === option.value}
                        value={option.value}
                      />
                      <Box>{option.name}</Box>
                    </Box>
                  )}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box
            sx={{
              minHeight: '20px',
            }}
          >
            <FormHelperText
              sx={{ color: !error && hasWarning ? 'warning.main' : undefined }}
            >
              {validationText || warningText || ' '}
            </FormHelperText>
          </Box>
        </FormControl>
      </SelectListWrapper>
    );
  },
);
